import type { IssueReport, IssueStatus } from '../../types/issue-triage';

/**
 * Every stage-to-stage move on the Issue Triage board (buttons on a card, buttons on the issue page,
 * and a drag between columns) is decided HERE, so the three can never disagree (L-1224).
 *
 * The server owns the truth (issueTriage_updateStatus accepts any stage from any stage). What it does
 * on its own, and the screen used to hide: a move to Reviewed lands in Needs Review unless the issue
 * has an owner AND a direction. This plan asks for those first instead of silently landing elsewhere.
 */

/** Stages a person can move an issue to. Analyzing is the system's own working state, never chosen; New ("send back to triage") is. */
export const MOVE_TARGETS: readonly IssueStatus[] = ['new', 'needs_review', 'reviewed', 'in_progress', 'done', 'dismissed'];

export const MOVE_LABEL: Readonly<Record<string, string>> = {
  new: 'New',
  needs_review: 'Needs Review',
  reviewed: 'Reviewed',
  in_progress: 'In Progress',
  done: 'Done',
  dismissed: 'Dismissed',
};

/** The server's pickup rule: an owner AND a non-blank direction. */
export function isReadyForReviewed(issue: Pick<IssueReport, 'owner' | 'developerNotes'>): boolean {
  return !!issue.owner?.uid && typeof issue.developerNotes === 'string' && issue.developerNotes.trim().length > 0;
}

/** Every stage this issue can be moved to: all of them except the one it is in (Analyzing counts as New). */
export function moveTargets(issue: Pick<IssueReport, 'status'>): { status: IssueStatus; label: string }[] {
  return MOVE_TARGETS.filter((s) => s !== issue.status && !(s === 'new' && issue.status === 'analyzing')).map((s) => ({ status: s, label: MOVE_LABEL[s] }));
}

export type MovePlan =
  | { kind: 'noop' }
  /** Dismissing records a reason; the board's Dismiss action does that. */
  | { kind: 'dismiss' }
  /** Reviewed needs an owner and a direction: ask for the missing ones, then move. */
  | { kind: 'needs-readiness'; missing: ('owner' | 'direction')[] }
  /** Send the status, with the status the board saw so a stale board is refused, not overwritten. */
  | { kind: 'status'; status: IssueStatus; expectedStatus: IssueStatus };

export function planMove(issue: IssueReport, target: IssueStatus): MovePlan {
  if (!MOVE_TARGETS.includes(target) || target === issue.status) return { kind: 'noop' };
  // The New column holds New and Analyzing: a drop within it changes nothing.
  if (target === 'new' && issue.status === 'analyzing') return { kind: 'noop' };
  if (target === 'dismissed') return { kind: 'dismiss' };
  if (target === 'reviewed' && !isReadyForReviewed(issue)) {
    const missing: ('owner' | 'direction')[] = [];
    if (!issue.owner?.uid) missing.push('owner');
    if (typeof issue.developerNotes !== 'string' || issue.developerNotes.trim().length === 0) missing.push('direction');
    return { kind: 'needs-readiness', missing };
  }
  return { kind: 'status', status: target, expectedStatus: issue.status };
}

/** The wording after a move: the stage it ACTUALLY landed in, never the one that was asked for. */
export function movedMessage(landed: IssueStatus): string {
  return `Moved to ${MOVE_LABEL[landed] ?? landed}`;
}

/**
 * After an owner + direction are saved, the server itself moves an issue between Needs Review and
 * Reviewed (statusAfterEdit). Only from other stages does Reviewed still need an explicit move, and
 * sending one from Needs Review would be refused as a stale board.
 */
export function needsExplicitMoveAfterEdits(status: IssueStatus): boolean {
  return status !== 'needs_review' && status !== 'reviewed';
}

/** Error codes where the write may have LANDED even though the call reported failure (a lost reply, a timeout). */
const MAY_HAVE_LANDED = /(deadline-exceeded|unavailable|internal|unknown|cancelled|aborted|data-loss)$/;

/**
 * Why a move failed, in words. The server's own reason for a stale refusal; for a timeout or lost reply it does NOT
 * claim nothing changed (it may have), it says the board is being checked; otherwise a plain "nothing was changed".
 */
export function moveFailureMessage(err: unknown): string {
  const e = err as { code?: string; serverMessage?: string } | null;
  if (e?.code?.endsWith('failed-precondition') && e.serverMessage) return e.serverMessage;
  if (!e?.code || MAY_HAVE_LANDED.test(e.code)) {
    return "That did not confirm, so it may not have saved. The board is refreshing: check where the issue is before trying again.";
  }
  return "Couldn't move this issue. Nothing was changed; refresh and try again.";
}
